/** Home: Needs-You escalations, your projects, and the authority's recent activity. */

import type { TemplateInstance } from 'hydrogen/runtime';
import { html, repeat } from 'hydrogen/runtime';

import type { ChangeRow, SurfaceSnapshot } from '../generated/surface.js';
import { changeDetailHref, PRODUCT_HOME_PATH, projectHref } from './nav.js';
import { NO_PROJECTS, NOT_READ, NotRead } from './consumer-projects.js';
import { projectEntries, type ProjectDirectory, type ProjectEntry } from './project-directory.js';
import type { SurfaceConnectionState } from './surface-connection.js';
import { acceptedIds, authorityState, authorityStateLabel, authorityStatusBadge, count, Digest, GapNotice, gapReason, ProductGap, projected, projectName, statusForChange, statusTone, Subject } from './product.js';
import { CardLink, KitBadge, KitTable, LinkButton, PageHeader, RowCard, Section } from './kit.js';
import { UnavailableCard } from './states.js';

type NeedTone = 'bad' | 'warn' | 'good' | 'neutral';

type Need = {
  /** The tone this need carries when it is non-zero. Zero is never an alarm. */
  tone: NeedTone;
  amount: number;
  singular: string;
  plural: string;
  detail: string;
  action: string;
  href: string;
};

function needsAttention(snapshot: SurfaceSnapshot, change: ChangeRow): boolean {
  const status = statusForChange(snapshot, change);
  return status === 'UNPROVABLE' || status === 'REJECTED';
}

function NeedCard(options: { tone: NeedTone; value: string; label: string; detail: string; action: string; href: string }): TemplateInstance {
  return html`
    <article class="ui-card mx-need" data-slot="card" data-tone="${options.tone}">
      <div class="ui-card-header" data-slot="card-header">
        <p class="ui-card-description" data-slot="card-description">${options.label}</p>
        <strong class="ui-card-title mx-need__value" data-slot="card-title">${options.value}</strong>
      </div>
      <div class="ui-card-content" data-slot="card-content"><p class="mx-need__detail">${options.detail}</p></div>
      <div class="ui-card-footer" data-slot="card-footer">${LinkButton({ href: options.href, label: options.action, variant: 'outline' })}</div>
    </article>
  `;
}

function Status(status: string): TemplateInstance {
  return KitBadge(status, statusTone(status));
}

function ChangeRows(snapshot: SurfaceSnapshot): TemplateInstance {
  return KitTable({
    label: 'Active changes',
    empty: 'No changes are stored in this authority.',
    rows: snapshot.recent_changes.slice(-6).reverse(),
    rowKey: (change) => change.id,
    columns: [
      {
        label: 'Change',
        render: (change) => {
          const href = changeDetailHref(change.id);
          return html`<a class="mx-cell-link" href="${href}" data-nav="${href}"><strong>${change.intent || 'No intent summary'}</strong><small>${Digest(change.id, 10)}</small></a>`;
        },
      },
      { label: 'State', render: (change) => Status(statusForChange(snapshot, change)) },
      { label: 'Needs attention', render: (change) => (needsAttention(snapshot, change) ? 'Yes' : 'No') },
    ],
  });
}

type StatusWord = { label: string; word: string; tone: 'good' | 'bad' | 'neutral' };

/**
 * A subsystem with nothing recorded is idle, not failing; one the authority
 * refuses to project is unknown. `present` is a tri-state so the grid never
 * reports a state nobody observed.
 */
function statusCells(snapshot: SurfaceSnapshot, runs: readonly unknown[] | undefined, runsReason: string): StatusWord[] {
  const cell = (label: string, present: boolean | undefined, failing = false): StatusWord =>
    failing ? { label, word: 'Failing', tone: 'bad' } : present === undefined ? { label, word: `Unknown: ${runsReason}`, tone: 'neutral' } : present ? { label, word: 'Active', tone: 'good' } : { label, word: 'Idle', tone: 'neutral' };
  return [
    cell('Intents', snapshot.counts.changes > 0),
    cell('Agents', snapshot.counts.unique_producers > 0),
    cell('Execution', runs === undefined ? undefined : runs.length > 0, snapshot.oracles.fail > 0),
    cell('Evidence', snapshot.counts.evidence > 0),
    cell('Storage', snapshot.counts.states > 0),
  ];
}

/** True when nothing on this surface is asking for a person. */
export function nothingNeedsYou(needs: readonly Need[]): boolean {
  return needs.every((need) => need.amount === 0);
}

/** Only live needs are drawn; a zero is not an alarm, and with none the page says so in one line. */
function Needs(needs: readonly Need[]): TemplateInstance {
  if (nothingNeedsYou(needs)) {
    return html`
      <div class="ui-alert" data-slot="alert" role="status">
        <div class="ui-alert-title" data-slot="alert-title">Nothing needs you</div>
        <div class="ui-alert-description" data-slot="alert-description">Every stored change is resolved and no oracle is failing.</div>
      </div>
    `;
  }
  return html`
    <div class="mx-needs">
      ${repeat(
        needs.filter((need) => need.amount > 0),
        (need) => need.href,
        (need) => NeedCard({ tone: need.tone, value: count(need.amount), label: need.amount === 1 ? need.singular : need.plural, detail: need.detail, action: need.action, href: need.href }),
      )}
    </div>
  `;
}

type KindChip = { kind: string | null; label: string; total: number; href: string; on: boolean };

/**
 * `?kind=` chips over the kinds the stream actually holds, plus All.
 *
 * Each count is the length of the same filter the chip applies, so a chip
 * cannot promise rows the stream will not show. Fresh objects every render so
 * Hydrogen `repeat` re-diffs the lit chip on a client navigation (#677).
 */
function kindChips(
  kinds: readonly string[],
  totals: Readonly<Record<string, number>>,
  all: number,
  active: string | null,
  search: string,
  path: string,
): KindChip[] {
  const rows: { kind: string | null; label: string; total: number }[] = [
    { kind: null, label: 'All', total: all },
    ...kinds.map((kind) => ({ kind, label: kind, total: totals[kind] ?? 0 })),
  ];
  return rows.map((row) => {
    const params = new URLSearchParams(search);
    if (row.kind === null) params.delete('kind');
    else params.set('kind', row.kind);
    const query = params.toString();
    return {
      ...row,
      href: query.length === 0 ? path : `${path}?${query}`,
      on: row.kind === active,
    };
  });
}

/**
 * What a row leads with, which is the summary when the authority wrote one.
 *
 * The fallback is the subject, and a subject is as often an identity as a
 * sentence -- so the fallback goes through the same cutter the row's second
 * line does rather than putting a whole digest in a bold prose line.
 */
function EventTitle(kind: string, subject: string, summary: string): TemplateInstance {
  if (summary.length > 0) return html`${summary}`;
  if (subject.length > 0) return Subject(subject);
  return html`${kind}`;
}

/**
 * The authority's recent events, newest first, filtered by `?kind=` on `path`.
 * A kind the stream does not hold (stale link) reads as All.
 */
export function ActivityFeed(snapshot: SurfaceSnapshot, search: string, path: string): TemplateInstance {
  const kinds = [...new Set(snapshot.recent_events.map((event) => event.kind))].sort((left, right) => left.localeCompare(right));
  const totals: Record<string, number> = {};
  for (const event of snapshot.recent_events) totals[event.kind] = (totals[event.kind] ?? 0) + 1;
  const requested = new URLSearchParams(search).get('kind');
  const active = requested !== null && kinds.includes(requested) ? requested : null;
  const events = snapshot.recent_events.filter((event) => active === null || event.kind === active).reverse();
  return Section({
    title: 'Activity',
    description: `${count(events.length)} projected events`,
    region: 'activity-feed',
    children: html`
      ${kinds.length === 0
        ? html``
        : html`<nav class="mx-chips" aria-label="Event kinds">
            ${repeat(
              kindChips(kinds, totals, snapshot.recent_events.length, active, search, path),
              (chip) => chip.kind ?? '',
              (chip) => html`<a class="ui-badge" data-slot="badge" data-variant="${chip.on ? 'default' : 'outline'}" href="${chip.href}" data-nav="${chip.href}" aria-current="${chip.on ? 'page' : 'false'}">${chip.label} ${count(chip.total)}</a>`,
            )}
          </nav>`}
      ${events.length === 0
        ? html`<p class="mx-empty-note" role="status">No authority events are projected.</p>`
        : html`<ol class="mx-timeline">
            ${repeat(
              events,
              (event, index) => `${event.kind}:${event.subject}:${index}`,
              (event) => html`<li><div><strong>${EventTitle(event.kind, event.subject, event.summary)}</strong><p>${Subject(event.subject)}</p></div><span class="ui-badge" data-slot="badge" data-variant="secondary">${event.kind}</span></li>`,
            )}
          </ol>`}
    `,
  });
}

/** Every project the account may open, with what has been read of each. */
function ProjectsSection(
  projects: readonly ProjectEntry[],
  directory: ProjectDirectory,
  status: (() => TemplateInstance) | null,
): TemplateInstance {
  return Section({
    title: 'Your projects',
    action: CardLink('/projects', 'All projects'),
    children: KitTable({
      label: 'Your projects',
      empty: directory === null ? 'No project authority is stored.' : NO_PROJECTS,
      rows: projects,
      rowKey: (row) => row.scope,
      card: (row) => {
        const href = projectHref('overview', {}, row.scope);
        return RowCard({
          title: html`<a class="mx-cell-link" href="${href}" data-nav="${href}">${row.role === null && row.reading ? projectName(row.reading) : row.name}</a>`,
          action: row.reading && status !== null ? status() : NotRead(),
          facts: [{ label: 'Changes', value: row.reading ? count(row.reading.counts.changes) : NotRead() }],
        });
      },
      columns: [
        {
          label: 'Project',
          render: (row) => {
            const href = projectHref('overview', {}, row.scope);
            const name = row.role === null && row.reading ? projectName(row.reading) : row.name;
            return html`<a class="mx-cell-link" href="${href}" data-nav="${href}"><strong>${name}</strong></a>`;
          },
        },
        { label: 'Changes', align: 'end', render: (row) => (row.reading ? count(row.reading.counts.changes) : NotRead()) },
        { label: 'Status', render: (row) => (row.reading && status !== null ? status() : NotRead()) },
      ],
    }),
  });
}

/**
 * The one place this product says "all systems". It is not allowed to say it
 * about a surface whose event stream is down, so the transport arrives here
 * beside the projection rather than being assumed live.
 */
export function ConsumerHome(
  snapshot: SurfaceSnapshot | null,
  connection: SurfaceConnectionState,
  directory: ProjectDirectory = null,
  /** With no projection: whether the read is still pending or has failed. */
  dataState: 'pending' | 'unavailable' = 'pending',
): TemplateInstance {
  if (snapshot === null) {
    return html`
      <section class="mx-page" data-region="consumer-home" data-data-state="${dataState}">
        ${PageHeader({ title: 'Needs You', description: 'Matrix only asks for your attention when machine resolution is insufficient.' })}
        ${ProjectsSection(projectEntries(directory, null), directory, null)}
        ${dataState === 'unavailable'
          ? UnavailableCard({ projectsLink: false })
          : Section({ title: 'Project data', children: html`<p class="mx-empty-note" role="status">Reading project data…</p>` })}
      </section>
    `;
  }
  const search = typeof globalThis.location === 'undefined' ? '' : (globalThis.location.search ?? '');
  const accepted = acceptedIds(snapshot);
  const executionRuns = projected(snapshot, 'fabric_runs');
  /*
   * Every need is a count over the same population. "Nothing needs you" is the
   * strongest claim on this page, and folding a window into it would answer
   * with a clear board over changes the authority has already declared it is
   * serving elsewhere -- so the population has to be projected before any of
   * these three counts means anything.
   */
  const changeWindow = projected(snapshot, 'recent_changes');
  const population = changeWindow ?? [];
  const decisions = population.filter((change) => needsAttention(snapshot, change));
  const ready = population.filter((change) => !accepted.has(change.id) && change.claims.length > 0);
  const emptyIntent = population.filter((change) => change.intent.trim() === '').length;
  const projects = projectEntries(directory, snapshot);
  const changes = projectHref('changes');
  const oracleFail = snapshot.oracles.fail;
  const state = authorityState(snapshot, connection);
  const needs: Need[] = [
    {
      tone: 'bad',
      amount: decisions.length,
      singular: 'decision required',
      plural: 'decisions required',
      detail: 'A human disposition is required to move forward.',
      action: 'Review Changes →',
      href: projectHref('changes', { filter: 'unresolved' }),
    },
    {
      tone: 'bad',
      amount: oracleFail,
      singular: 'failing oracle',
      plural: 'failing oracles',
      detail: 'No risk model is authorized. Failing oracles are the stored policy signal.',
      action: 'Review Evidence →',
      href: projectHref('checks'),
    },
    {
      tone: 'good',
      amount: ready.length,
      singular: 'change ready for composition',
      plural: 'changes ready for composition',
      detail: 'Stored changes have claims and no accepting certificate.',
      action: 'Compose Changes →',
      href: changes,
    },
    {
      tone: 'warn',
      amount: emptyIntent,
      singular: 'intent is ambiguous',
      plural: 'intents are ambiguous',
      detail: 'These stored changes have an empty intent summary.',
      action: 'Review Changes →',
      href: changes,
    },
  ];
  return html`
    <section class="mx-page" data-region="consumer-home">
      ${PageHeader({ title: 'Needs You', description: 'Matrix only asks for your attention when machine resolution is insufficient.' })}
      ${changeWindow ? Needs(needs) : GapNotice(snapshot, 'recent_changes')}
      <div class="mx-grid">
        ${ProjectsSection(projects, directory, () => Status(authorityStatusBadge(state)))}
        ${Section({ title: 'Active changes', action: CardLink(changes, 'View all'), children: ChangeRows(snapshot) })}
      </div>
      ${ActivityFeed(snapshot, search, PRODUCT_HOME_PATH)}
      <div class="mx-grid mx-grid--three">
        ${Section({
          title: 'Spend & usage',
          children: ProductGap({
            title: 'Spend is not metered by this authority',
            detail: 'Cost per change, monthly cap and usage to date appear here once the authority meters them. Nothing on this surface can estimate them in the meantime.',
          }),
        })}
        ${Section({
          title: 'Authority totals',
          children: html`<dl class="mx-facts">
            <div><dt>Certified changes</dt><dd>${count(accepted.size)}</dd></div>
            <div><dt>Oracle passes</dt><dd>${count(snapshot.oracles.pass)}</dd></div>
            <div><dt>Evidence in the recent window</dt><dd>${count(snapshot.counts.evidence)}</dd></div>
          </dl>`,
        })}
        ${Section({
          title: 'System status',
          children: html`<dl class="mx-facts">
            <div><dt>Authority</dt><dd>${KitBadge(authorityStateLabel(state), state === 'operational' ? 'good' : state === 'attention' ? 'bad' : 'neutral')}</dd></div>
            ${repeat(statusCells(snapshot, executionRuns, gapReason(snapshot, 'fabric_runs')), (cell) => cell.label, (cell) => html`<div><dt>${cell.label}</dt><dd>${KitBadge(cell.word, cell.tone)}</dd></div>`)}
          </dl>
          <p class="mx-empty-note">Uptime is not projected</p>`,
        })}
      </div>
    </section>
  `;
}
